import { PACKAGE_FILES, SECTION_IDS, type DocumentSection, type PackageFileKey, type PackageFileSpec, type PackageProvider, type PackageSectionSelector } from "../../types/evidence";

// By what the file holds, so one kind keeps its hue across processors; the brand blue is the whole document.
export const FILE_SWATCHES = {
  uncategorized: "#465fff",
  other: "#465fff",
  customer_communication: "#0f9d8a",
  shipping_documentation: "#d9752b",
  proof_of_fulfillment: "#d9752b",
  refund_policy: "#8e44ad",
  cancellation_policy: "#6a1b9a",
  return_policy: "#8e44ad",
  service_documentation: "#3f9142",
  receipt: "#b8860b",
  proof_of_refund: "#c2185b",
  defense_material: "#465fff",
  tid_or_invoice: "#b8860b",
  proof_of_goods_provided: "#d9752b",
  disclosure_at_poi: "#8e44ad",
} as const satisfies Record<PackageFileKey, string>;

// The first category file holding the section (or its rendered `id`), else the whole document or the rest file with its upload slot. The policies
// row reads as its refund policy and names the cancellation policy's file too when that is another.
export function packageFileOf(provider: PackageProvider, section: DocumentSection, id?: string): { file: PackageFileKey; label: string; swatch: string } {
  if (section === "policies" && !id) {
    const refund = holderOf(provider, section, "refund-policy");
    const labels = new Set([holderOf(provider, section, "cancellation-policy").label, refund.label]);
    return { ...refund, label: [...labels].join(" · ") };
  }
  return holderOf(provider, section, id);
}

// The section a rendered `data-section` id belongs to; the case header and the conclusion are not sections.
export function sectionOfId(id: string): DocumentSection | "conclusion" | "case" | null {
  if (id === "conclusion") return "conclusion";
  if (id === "case-header") return "case";
  return (Object.keys(SECTION_IDS) as DocumentSection[]).find((key) => (SECTION_IDS[key] as readonly string[]).includes(id)) ?? null;
}

function holderOf(provider: PackageProvider, section: DocumentSection, id: string | undefined) {
  const entries = Object.entries(PACKAGE_FILES[provider]) as [PackageFileKey, PackageFileSpec][];
  const holds = (selector: PackageSectionSelector) => typeof selector === "string"
    ? selector === section
    : selector.section === section && (id === undefined || (selector.ids as readonly string[]).includes(id));
  const [file, spec] = entries.find(([, spec]) => "sections" in spec && spec.sections.some(holds)) ?? entries.find(([, spec]) => !("sections" in spec))!;
  return { file, label: "sections" in spec ? spec.label : `${spec.label} · Upload as ${spec.slot}`, swatch: FILE_SWATCHES[file] };
}
